<template>
  <modal-dialog
    ref="modal"
    :visible="dialog"
    :title="$t('title.employee')"
    persistent
    scrollable
    max-width="400"
    container-class="pa-4 pb-0"
    card-class="pa-0"
  >
    <div
      class="employee-dialog"
      :class="{ 'employee-dialog--compact': compactList }"
    >
      <v-list
        class="pa-0"
        nav
        dense
      >
        <v-list-item-group
          v-model="selection"
          color="primary"
        >
          <v-list-item
            v-for="e in employees"
            :key="e.id"
            :value="e.id"
            class="employee-dialog__item"
            :class="compactList ? 'px-2 mb-1' : 'px-3 mb-2'"
            ripple
          >
            <v-list-item-avatar
              color="blue"
              :size="compactList ? 32 : 40"
            >
              <span
                class="white--text font-weight-medium"
                :class="compactList ? 'text-body-2' : 'subtitle-1'"
              >{{ initials(e.name) }}</span>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title
                class="font-weight-medium"
                :class="compactList ? 'text-body-2' : 'text-body-1'"
              >
                {{ e.name }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="ma-0">
              <v-icon
                v-if="selection === e.id"
                color="primary"
                small
              >
                mdi-check-circle
              </v-icon>
            </v-list-item-action>
          </v-list-item>

          <v-list-item
            :value="0"
            class="employee-dialog__item employee-dialog__item--new"
            :class="compactList ? 'px-2 mb-1' : 'px-3 mb-2'"
            ripple
          >
            <v-list-item-avatar
              color="blue"
              :size="compactList ? 32 : 40"
            >
              <v-icon
                color="white"
                :small="compactList"
              >
                mdi-account-plus
              </v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title
                class="font-weight-medium"
                :class="compactList ? 'text-body-2' : 'text-body-1'"
              >
                {{ $t('label.new_employee') }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="ma-0">
              <v-icon
                v-if="selection === 0"
                color="primary"
                small
              >
                mdi-check-circle
              </v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list-item-group>
      </v-list>

      <v-expand-transition>
        <div
          v-if="selection === 0"
          ref="newEmployeeContainer"
          class="pt-1"
        >
          <v-text-field
            ref="newEmployeeField"
            v-model="newEmployeeName"
            outlined
            dense
            hide-details="auto"
            :label="$t('label.employee_name')"
            :error-messages="newEmployeeFieldErrors"
            autocomplete="off"
            @input="employeeError = ''"
          />
        </div>
      </v-expand-transition>

      <div
        v-if="employeeError && selection !== 0"
        class="text-caption error--text my-2"
        role="alert"
      >
        {{ employeeError }}
      </div>
    </div>
    <template #actions>
      <div class="d-flex flex-column w-100">
        <v-checkbox
          v-model="rememberMe"
          class="ma-0 mb-2 mt-0"
          :label="$t('label.remember_my_name')"
          hide-details
        />

        <div class="d-flex align-center">
          <v-spacer />
          <v-btn
            color="success"
            depressed
            :disabled="selection === null"
            @click="save"
          >
            <div class="mx-6">
              {{ $t('button.save') }}
            </div>
          </v-btn>
          <v-btn
            color="error"
            text
            @click="cancel"
          >
            {{ $t('button.cancel') }}
          </v-btn>
        </div>
      </div>
    </template>
  </modal-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import DialogClosable from '@/mixins/dialog-closable';
import { mapActions } from 'vuex';
import Employee from '@/model/Employee';

export default Vue.extend({
  name: 'EmployeeDialog',
  mixins: [DialogClosable],
  props: {

  },
  data: () => ({
    selection: null as number | null,
    newEmployeeName: '',
    rememberMe: false,
    employeeError: '',
  }),
  computed: {
    employees(): Employee[] {
      return this.$tstore.state.settings.employees;
    },
    newEmployeeFieldErrors(): string[] {
      if (this.selection !== 0 || !this.employeeError) {
        return [];
      }
      return [this.employeeError];
    },
    compactList(): boolean {
      return this.employees.length > 10;
    },
  },
  watch: {
    visible(value: boolean) {
      if (value) {
        this.reset();
      }
    },
    selection(val: number | null) {
      this.employeeError = '';
      if (val === 0) {
        this.$nextTick(() => {
          window.setTimeout(() => {
            const modal = this.$refs.modal as Vue & { $refs?: Record<string, unknown> };
            const scrollEl = modal?.$refs?.scrollContent as HTMLElement | undefined;
            const container = this.$refs.newEmployeeContainer as HTMLElement | undefined;

            if (scrollEl && container) {
              const top = container.offsetTop - 16;
              if (typeof scrollEl.scrollTo === 'function') {
                scrollEl.scrollTo({ top, behavior: 'smooth' });
              } else {
                scrollEl.scrollTop = top;
              }
            } else {
              container?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
            }

            const field = this.$refs.newEmployeeField as Vue & { focus?: () => void };
            field?.focus?.();
          }, 250);
        });
      }
    },
  },
  mounted() {
    this.reset();
  },
  methods: {
    ...mapActions(['setLastEmployee']),
    initials(name: string): string {
      if (!name?.trim()) {
        return '?';
      }
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) {
        return '?';
      }
      if (parts.length === 1) {
        const w = parts[0];
        if (w.length <= 1) {
          return w.toUpperCase();
        }
        return w[0].toUpperCase() + w[1].toLowerCase();
      }
      const a = parts[0][0];
      const b = parts[parts.length - 1][0];
      if (!a || !b) {
        return '?';
      }
      return (a + b).toUpperCase();
    },
    reset() {
      this.selection = null;
      this.employeeError = '';

      const { lastEmployee, rememberEmployee } = this.$tstore.state.reservation;

      this.rememberMe = rememberEmployee;
      if (!rememberEmployee) {
        this.selection = null;
      } else if (lastEmployee?.id === 0) {
        this.selection = this.employees.find((o) => o.name === lastEmployee?.name)?.id ?? null;
      } else {
        this.selection = lastEmployee?.id ?? null;
      }

      this.newEmployeeName = '';
    },
    save() {
      if (this.selection === null || this.selection === undefined) {
        this.employeeError = this.$i18n.tc('error.no_employee_selected');
        return;
      }

      if (this.selection === 0 && this.newEmployeeName.trim().length < 2) {
        this.employeeError = this.$i18n.tc('error.no_employee_selected');
        return;
      }

      this.employeeError = '';

      const remember = this.rememberMe;
      const employee = this.selection === 0
        ? new Employee({ employeeId: 0, employeeName: this.newEmployeeName.trim() })
        : this.employees.find(((e) => e.id === this.selection));

      this.setLastEmployee({ employee, remember });

      this.$emit('selected', { employee, remember });
    },
    cancel() {
      this.$emit('close', false);
    },
  },
});
</script>

<style scoped lang="scss">
@import '@/scss/variables';

.employee-dialog {
  &--compact {
    .employee-dialog__item {
      min-height: 44px !important;

      ::v-deep .v-list-item__avatar {
        align-self: center;
        margin-top: 4px !important;
        margin-bottom: 4px !important;
      }
    }
  }

  &__item {
    border: 1px solid $grey-15-color;
    transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

    .theme--dark & {
      border-color: $grey-75-color;
    }
  }
}
</style>
